<template>
  <BittsVirtualScrollList
    v-if="useVirtualScroll"
    :options="visibleRows"
    :item-height="virtualScrollItemHeight"
    :max-height="virtualScrollMaxHeight"
    :get-item-key="getRowKey"
    :has-dynamic-item-height="true"
  >
    <template #default="{ item: row }">
      <div class="bitts-tree__virtual-row">
        <span
          v-for="level in row.depth"
          :key="level"
          class="bitts-tree__indent"
        />
        <BittsTreeRow
          class="flex-1 min-w-0"
          :node="row.node"
          :checked="row.checked"
          :indeterminate="row.indeterminate"
          :has-children="nodeHasChildren(row.node)"
          :is-expanded="isExpanded(row.node.id)"
          :is-toggle-locked="expandAll || isExpansionLocked"
          :is-disabled
          :highlight-term
          :accessible-name="row.accessibleName"
          @toggle="toggleExpand(row.node.id)"
          @check="(value) => onRowCheckboxChange(value, row)"
        >
          <template #node-checkbox="slotProps">
            <slot name="node-checkbox" v-bind="slotProps" />
          </template>
          <template #node-prefix="{ node: nodeSlot }">
            <slot name="node-prefix" :node="nodeSlot" />
          </template>
          <template #node-content="{ node: nodeSlot }">
            <slot
              name="node-content"
              :node="nodeSlot"
              :highlight-term="highlightTerm"
            />
          </template>
          <template #node-suffix="{ node: nodeSlot }">
            <slot name="node-suffix" :node="nodeSlot" />
          </template>
        </BittsTreeRow>
      </div>
    </template>
  </BittsVirtualScrollList>
  <div v-else>
    <div
      v-for="{ node, indeterminate, checked } in nodesWithState"
      :key="node.id"
      class="flex flex-col"
    >
      <BittsTreeRow
        :node
        :checked
        :indeterminate
        :has-children="nodeHasChildren(node)"
        :is-expanded="isExpanded(node.id)"
        :is-toggle-locked="expandAll || isExpansionLocked"
        :is-disabled
        :highlight-term
        @toggle="toggleExpand(node.id)"
        @check="(value) => onCheckboxChange(value, node)"
      >
        <template #node-checkbox="slotProps">
          <slot name="node-checkbox" v-bind="slotProps" />
        </template>
        <template #node-prefix="{ node: nodeSlot }">
          <slot name="node-prefix" :node="nodeSlot" />
        </template>
        <template #node-content="{ node: nodeSlot }">
          <slot
            name="node-content"
            :node="nodeSlot"
            :highlight-term="highlightTerm"
          />
        </template>
        <template #node-suffix="{ node: nodeSlot }">
          <slot name="node-suffix" :node="nodeSlot" />
        </template>
      </BittsTreeRow>
      <div
        v-if="isExpanded(node.id) && nodeHasChildren(node)"
        role="group"
        :aria-label="node.label"
        class="ml-28 pl-4 border-l border-neutral-border flex flex-col"
      >
        <BittsBaseTree
          :is-disabled
          :node-data="node.children ?? []"
          :get-node-indeterminate="getNodeIndeterminate"
          @update="
            (updatedNode, updatedNodeId) =>
              handleChildUpdate(updatedNode, node, updatedNodeId)
          "
          :expand-all="expandAll"
          :is-expansion-locked="isExpansionLocked"
          :highlight-term="highlightTerm"
        >
          <template #node-checkbox="slotProps">
            <slot name="node-checkbox" v-bind="slotProps" />
          </template>
          <template #node-prefix="{ node: nodeSlot }">
            <slot name="node-prefix" :node="nodeSlot" />
          </template>
          <template #node-content="{ node: nodeSlot }">
            <slot
              name="node-content"
              :node="nodeSlot"
              :highlight-term="highlightTerm"
            />
          </template>
          <template #node-suffix="{ node: nodeSlot }">
            <slot name="node-suffix" :node="nodeSlot"></slot>
          </template>
        </BittsBaseTree>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts" generic="TMetaData">
import { computed } from 'vue';

import BittsVirtualScrollList from '../BittsVirtualScrollList/BittsVirtualScrollList.vue';

import BittsTreeRow from './BittsTreeRow.vue';
import { type TreeNodeId, useTreeExpansion } from './useTreeExpansion';

export type TreeNodeType<TMetaData = unknown> = {
  label: string;
  checked?: boolean;
  indeterminate?: boolean;
  children?: TreeNodeType<TMetaData>[];
  id: number | string;
  isDisabled?: boolean;
  isCheckable?: boolean;
  metaData?: TMetaData;
};

type Node = TreeNodeType<TMetaData>;

type VisibleRowType = {
  key: string;
  node: Node;
  depth: number;
  ancestorIds: TreeNodeId[];
  accessibleName: string;
  checked: boolean | undefined;
  indeterminate: boolean;
};

const {
  expandAll = false,
  isDisabled = false,
  isExpansionLocked = false,
  initialExpandedNodes = {},
  forceExpandedIds = [],
  highlightTerm = '',
  nodeData,
  getNodeIndeterminate,
  useVirtualScroll = false,
  virtualScrollItemHeight = 24,
  virtualScrollMaxHeight = 400,
} = defineProps<{
  isDisabled?: boolean;
  nodeData: Node[];
  expandAll?: boolean;
  isExpansionLocked?: boolean;
  initialExpandedNodes?: Record<number | string, boolean>;
  forceExpandedIds?: (number | string)[];
  highlightTerm?: string;
  getNodeIndeterminate?: (node: Node) => boolean;
  useVirtualScroll?: boolean;
  virtualScrollItemHeight?: number;
  virtualScrollMaxHeight?: number;
}>();

const emit = defineEmits<{
  update: [value: Node[], updatedNodeId: number | string];
}>();
defineSlots<{
  'node-checkbox'?(props: {
    node: Node;
    checked: boolean | undefined;
    indeterminate: boolean;
    onInput: (checked: boolean) => void;
    disabled: boolean;
  }): unknown;
  'node-prefix'?(props: { node: Node }): unknown;
  'node-content'?(props: { node: Node; highlightTerm: string }): unknown;
  'node-suffix'?(props: { node: Node }): unknown;
}>();

const expansion = useTreeExpansion({
  initialExpandedNodes: () => initialExpandedNodes,
  forceExpandedIds: () => forceExpandedIds,
  isExpansionLocked: () => isExpansionLocked,
});

const nodeHasChildren = (node: Node) => {
  return !!node.children && node.children.length > 0;
};

const isExpanded = (id: TreeNodeId) => {
  return expandAll || expansion.isExpanded(id);
};

const toggleExpand = (id: TreeNodeId) => {
  expansion.toggle(id);
};

const isIndeterminate = (node: Node): boolean => {
  if (getNodeIndeterminate) return getNodeIndeterminate(node);
  if (node.indeterminate !== undefined) return node.indeterminate;
  const children = (node.children || []).filter(
    (child) => child.isCheckable !== false && !child.isDisabled,
  );
  if (!children.length) return false;
  const someChecked = children.some((child) => child.checked);
  const someIndeterminate = children.some((child) => child.indeterminate);
  return !node.checked && (someChecked || someIndeterminate);
};

function withState(node: Node) {
  const indeterminate = isIndeterminate(node);
  return {
    node,
    indeterminate,
    checked: indeterminate ? false : node.checked,
  };
}

const nodesWithState = computed(() => nodeData.map(withState));

/** Flat rows have no nested group to tell a screen reader which node a
 * child belongs to, so each row's name carries its ancestors, nearest first. */
const visibleRows = computed(() => {
  const rows: VisibleRowType[] = [];
  function visit(
    nodes: Node[],
    depth: number,
    ancestorIds: TreeNodeId[],
    ancestorLabels: string[],
  ) {
    nodes.forEach((node) => {
      rows.push({
        ...withState(node),
        key: [...ancestorIds, node.id].join('/'),
        depth,
        ancestorIds,
        accessibleName: [node.label, ...ancestorLabels].join(', '),
      });
      if (isExpanded(node.id) && nodeHasChildren(node)) {
        visit(
          node.children ?? [],
          depth + 1,
          [...ancestorIds, node.id],
          [node.label, ...ancestorLabels],
        );
      }
    });
  }
  visit(nodeData, 0, [], []);
  return rows;
});

function getRowKey(row: VisibleRowType) {
  return row.key;
}

const onEmitUpdate = (updatedNodes: Node[], updatedNodeId: number | string) => {
  emit('update', updatedNodes, updatedNodeId);
};

const onCheckboxChange = (checked: boolean, node: Node) => {
  handleNodeUpdate(withCheckedSubtree(node, checked), node.id);
};

function onRowCheckboxChange(checked: boolean, row: VisibleRowType) {
  onEmitUpdate(
    replaceAlongPath(
      nodeData,
      row.ancestorIds,
      withCheckedSubtree(row.node, checked),
    ),
    row.node.id,
  );
}

function withCheckedSubtree(node: Node, checked: boolean): Node {
  return {
    ...node,
    checked,
    children: updateChildrenCheck(node.children, checked),
  };
}

const updateChildrenCheck = (
  children: Node[] | undefined,
  checked: boolean,
): Node[] | undefined => {
  if (!children) return undefined;

  return children.map((child) => ({
    ...child,
    ...(child.isCheckable === false || child.isDisabled ? {} : { checked }),
    children: updateChildrenCheck(child.children, checked),
  }));
};

function withRolledUpCheck(parent: Node, children: Node[]): Node {
  const checkableChildren = children.filter(
    (child) => child.isCheckable !== false && !child.isDisabled,
  );
  return {
    ...parent,
    children,
    ...(parent.isCheckable === false
      ? {}
      : {
          checked:
            checkableChildren.length > 0 &&
            checkableChildren.every((child) => child.checked),
        }),
  };
}

function replaceAlongPath(
  nodes: Node[],
  ancestorIds: TreeNodeId[],
  updatedNode: Node,
): Node[] {
  const [ancestorId, ...remainingIds] = ancestorIds;
  if (ancestorId === undefined) {
    return nodes.map((node) =>
      node.id === updatedNode.id ? updatedNode : node,
    );
  }
  return nodes.map((node) =>
    node.id === ancestorId
      ? withRolledUpCheck(
          node,
          replaceAlongPath(node.children ?? [], remainingIds, updatedNode),
        )
      : node,
  );
}

const handleNodeUpdate = (
  updatedNode: Node,
  updatedNodeId: number | string,
) => {
  const updatedNodeData = nodeData.map((node) =>
    node.id === updatedNode.id ? updatedNode : node,
  );
  onEmitUpdate(updatedNodeData, updatedNodeId);
};

const handleChildUpdate = (
  updatedChildren: Node[],
  parent: Node,
  updatedNodeId: number | string,
) => {
  handleNodeUpdate(withRolledUpCheck(parent, updatedChildren), updatedNodeId);
};
</script>

<style scoped lang="pcss">
@reference "../../../../apps/crossbeam-app/src/css/theme.css";
.bitts-tree__virtual-row {
  @apply flex w-full;
}

.bitts-tree__indent {
  @apply ml-28 pl-4 border-l border-neutral-border self-stretch;
}
</style>
